import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {useNavigate} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {X, Sparkles, Share2} from 'lucide-react';import {Button} from '@/components/ui/button';import {toast} from 'sonner';import axios from 'axios';
import Header from '@/components/Header';
import SettingsPage from '@/pages/SettingsPage';
import {useTheme} from '@/contexts/ThemeContext';import BibleReader from '@/components/BibleReader';
import {API_URL as API} from '@/utils/apiConfig';
import ShareRecapModal from '@/components/ShareRecapModal';
import ShareRecapTopicModal from '@/components/ShareRecapTopicModal';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';


// Theme-aware text colors
const getThemeTextColors = (colorMode) => {
  const isDark = colorMode === 'dark';
  return {
    primary: isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
    secondary: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(51, 65, 85, 0.8)',
    muted: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(71, 85, 105, 0.6)',
    isDark
  };
};

// Glass-like Floating Bubble Component with smooth animation
const GlassBubble = ({ bubble, index, onClick, totalBubbles, themeColors, clearMode }) => {
  const positionRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const [displayPosition, setDisplayPosition] = useState({ x: 50, y: 50 });
  const animationRef = useRef(null);
  const initializedRef = useRef(false);
  const timeRef = useRef(0);
  
  // Calculate dynamic size based on total bubbles
  const bubbleSize = useMemo(() => {
    const baseSizes = {
      large: { mobile: 105, desktop: 145 },
      medium: { mobile: 85, desktop: 115 },
      small: { mobile: 68, desktop: 90 }
    };
    
    const scaleFactor = totalBubbles > 10 ? 0.8 : totalBubbles > 6 ? 0.9 : 1;
    const size = baseSizes[bubble.size] || baseSizes.small;
    
    return {
      mobile: Math.round(size.mobile * scaleFactor),
      desktop: Math.round(size.desktop * scaleFactor)
    };
  }, [bubble.size, totalBubbles]);
  
  // Initialize position
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const cols = Math.ceil(Math.sqrt(totalBubbles));
    const rows = Math.ceil(totalBubbles / cols);
    const row = Math.floor(index / cols);
    const col = index % cols;
    
    const cellWidth = 75 / cols;
    const cellHeight = 65 / rows;
    
    const baseX = 12 + col * cellWidth + cellWidth / 2;
    const baseY = 15 + row * cellHeight + cellHeight / 2;
    
    const randomX = baseX + (Math.random() - 0.5) * cellWidth * 0.5;
    const randomY = baseY + (Math.random() - 0.5) * cellHeight * 0.4;
    
    const initialX = Math.max(10, Math.min(90, randomX));
    const initialY = Math.max(12, Math.min(82, randomY));
    
    positionRef.current = { x: initialX, y: initialY };
    targetRef.current = { x: initialX, y: initialY };
    setDisplayPosition({ x: initialX, y: initialY });
  }, [index, totalBubbles]);
  
  // Ultra-smooth floating animation with easing
  useEffect(() => {
    const baseSpeed = 0.00008 + (index % 5) * 0.00002;
    const amplitudeX = 6 + (index % 4) * 1.5;
    const amplitudeY = 8 + (index % 3) * 2;
    const phaseX = (index * 0.7) % (Math.PI * 2);
    const phaseY = (index * 1.3) % (Math.PI * 2);
    
    const animate = (timestamp) => {
      if (!timeRef.current) timeRef.current = timestamp;
      const elapsed = timestamp - timeRef.current;
      
      // Very slow, smooth sine wave motion
      const newTargetX = positionRef.current.x + Math.sin(elapsed * baseSpeed + phaseX) * amplitudeX * 0.15;
      const newTargetY = positionRef.current.y + Math.sin(elapsed * baseSpeed * 0.8 + phaseY) * amplitudeY * 0.15;
      
      // Smooth interpolation (easing)
      targetRef.current.x += (newTargetX - targetRef.current.x) * 0.02;
      targetRef.current.y += (newTargetY - targetRef.current.y) * 0.02;
      
      // Keep within bounds
      const boundedX = Math.max(8, Math.min(92, targetRef.current.x));
      const boundedY = Math.max(10, Math.min(85, targetRef.current.y));
      
      setDisplayPosition({ x: boundedX, y: boundedY });
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [index]);
  
  // Clear mode colors
  const clearBg = themeColors.isDark
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(255,255,255,0.35)';
  const clearBorder = themeColors.isDark
    ? 'rgba(255,255,255,0.18)'
    : 'rgba(255,255,255,0.6)';
  const clearHighlight = themeColors.isDark
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(255,255,255,0.7)';
  const clearShadow = themeColors.isDark
    ? '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.15), inset 0 -2px 6px rgba(255,255,255,0.05)'
    : '0 8px 32px rgba(0,0,0,0.08), inset 0 2px 4px rgba(255,255,255,0.8), inset 0 -3px 6px rgba(0,0,0,0.04)';
  const clearTextColor = themeColors.isDark ? 'rgba(255,255,255,0.85)' : 'rgba(30,41,59,0.8)';
  const clearTextShadow = themeColors.isDark
    ? '0 1px 3px rgba(0,0,0,0.5)'
    : '0 1px 2px rgba(255,255,255,0.8)';

  return (
    <button
      onClick={() => onClick(bubble)}
      className="absolute focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-transparent rounded-full group"
      style={{
        left: `${displayPosition.x}%`,
        top: `${displayPosition.y}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: bubble.size === 'large' ? 20 : bubble.size === 'medium' ? 10 : 5,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'left, top'
      }}
      data-testid={`bubble-${bubble.topic_id}`}
    >
      {/* Soft outer glow */}
      {!clearMode && (
        <div
          className="absolute inset-0 rounded-full opacity-30 group-hover:opacity-50"
          style={{
            background: `radial-gradient(circle, ${bubble.color} 0%, transparent 70%)`,
            transform: 'scale(1.8)',
            filter: 'blur(20px)',
            transition: 'opacity 0.4s ease'
          }}
        />
      )}
      
      {/* Glass bubble container */}
      <div
        className="relative rounded-full flex items-center justify-center overflow-hidden cursor-pointer group-hover:scale-105"
        style={{
          width: `${bubbleSize.mobile}px`,
          height: `${bubbleSize.mobile}px`,
          background: clearMode
            ? `radial-gradient(ellipse at 30% 20%, ${clearHighlight} 0%, ${clearBg} 50%, transparent 70%)`
            : `linear-gradient(135deg, 
              ${bubble.color.replace('0.7', '0.45')} 0%, 
              ${bubble.color.replace('0.7', '0.25')} 40%,
              ${bubble.color.replace('0.7', '0.35')} 100%)`,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: clearMode
            ? `1.5px solid ${clearBorder}`
            : '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: clearMode
            ? clearShadow
            : `0 8px 32px ${bubble.color.replace('0.7', '0.25')},
               inset 0 2px 4px rgba(255, 255, 255, 0.25),
               inset 0 -2px 4px rgba(0, 0, 0, 0.1)`,
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s ease'
        }}
      >
        {/* Glass highlight reflection */}
        <div 
          className="absolute top-1.5 left-3 w-2/5 h-1/4 rounded-full"
          style={{
            background: clearMode
              ? `linear-gradient(180deg, ${themeColors.isDark ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.9)'} 0%, transparent 100%)`
              : 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)',
            opacity: clearMode ? 0.8 : 0.6
          }}
        />

        {/* Secondary reflection for clear mode (bottom-right catchlight) */}
        {clearMode && (
          <div
            className="absolute bottom-2 right-3 w-1/5 h-1/6 rounded-full"
            style={{
              background: `radial-gradient(ellipse, ${themeColors.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)'} 0%, transparent 100%)`
            }}
          />
        )}

        {/* Iridescent rim for clear mode */}
        {clearMode && (
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: `conic-gradient(from 180deg, transparent 0%, ${themeColors.isDark ? 'rgba(180,200,255,0.08)' : 'rgba(180,200,255,0.15)'} 25%, transparent 50%, ${themeColors.isDark ? 'rgba(255,180,220,0.06)' : 'rgba(255,180,220,0.12)'} 75%, transparent 100%)`,
              mask: 'radial-gradient(circle, transparent 60%, black 80%, black 100%)',
              WebkitMask: 'radial-gradient(circle, transparent 60%, black 80%, black 100%)'
            }}
          />
        )}
        
        {/* Text label */}
        <span 
          className="relative z-10 font-semibold text-center leading-tight"
          style={{
            color: clearMode ? clearTextColor : '#FFFFFF',
            fontSize: bubbleSize.mobile > 100 ? '0.875rem' : bubbleSize.mobile > 80 ? '0.75rem' : '0.625rem',
            textShadow: clearMode ? clearTextShadow : '0 1px 3px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)',
            maxWidth: `${bubbleSize.mobile - 16}px`,
            overflow: 'hidden',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            padding: '0 4px'
          }}
        >
          {bubble.label}
        </span>
      </div>
      
      {/* Desktop size override */}
      <style>{`
        @media (min-width: 768px) {
          button[data-testid="bubble-${bubble.topic_id}"] > div:nth-child(2) {
            width: ${bubbleSize.desktop}px !important;
            height: ${bubbleSize.desktop}px !important;
          }
          button[data-testid="bubble-${bubble.topic_id}"] > div:nth-child(2) > span {
            font-size: ${bubbleSize.desktop > 110 ? '0.9375rem' : '0.8125rem'} !important;
            max-width: ${bubbleSize.desktop - 20}px !important;
          }
        }
      `}</style>
    </button>
  );
};

// Enhanced Glass-morphism Summary Modal with high-contrast text
const GlassSummaryModal = ({ topic, summary, loading, onClose, onShare, themeColors, clearMode }) => {
  const { t } = useTranslation();
  
  if (!topic) return null;
  
  const baseColor = topic.color || 'rgba(99, 102, 241, 0.7)';
  
  // Clear mode styling
  const modalBg = clearMode
    ? (themeColors.isDark
      ? 'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.06) 100%)'
      : 'linear-gradient(160deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.6) 100%)')
    : `linear-gradient(160deg, 
        ${baseColor.replace('0.7', '0.7')} 0%, 
        ${baseColor.replace('0.7', '0.5')} 40%,
        ${baseColor.replace('0.7', '0.6')} 100%)`;
  const modalBorder = clearMode
    ? (themeColors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)')
    : 'rgba(255, 255, 255, 0.25)';
  const textColor = clearMode ? themeColors.primary : '#FFFFFF';
  const subTextColor = clearMode ? themeColors.secondary : 'rgba(255,255,255,0.9)';
  const mutedColor = clearMode ? themeColors.muted : 'rgba(255,255,255,0.6)';
  const cardBg = clearMode
    ? (themeColors.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.4)')
    : 'rgba(255, 255, 255, 0.12)';
  const cardBorder = clearMode
    ? (themeColors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)')
    : 'rgba(255, 255, 255, 0.15)';
  const dotColor = clearMode ? themeColors.secondary : 'rgba(255,255,255,0.7)';
  const closeBtnBg = clearMode
    ? (themeColors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
    : 'rgba(255, 255, 255, 0.15)';
  const closeBtnBorder = clearMode
    ? (themeColors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)')
    : 'rgba(255, 255, 255, 0.2)';
  const iconColor = clearMode ? themeColors.primary : '#FFFFFF';
  
  return (
    <div 
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Frosted glass backdrop */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'rgba(0, 0, 0, 0.35)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)'
        }}
      />
      {/* Modal content - translucent glass panel */}
      <div 
        className="relative w-full max-w-lg overflow-y-auto rounded-3xl animate-in zoom-in-95 fade-in duration-300"
        style={{
          background: modalBg,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: `1px solid ${modalBorder}`,
          maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
          boxShadow: `
            0 32px 64px -16px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(0, 0, 0, 0.1)
          `
        }}
        onClick={e => e.stopPropagation()}
        data-testid="summary-modal"
      >
        {/* Top glass highlight */}
        <div 
          className="absolute top-0 left-0 right-0 h-20 rounded-t-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)'
          }}
        />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2.5 rounded-full z-20 transition-all"
          style={{
            background: closeBtnBg,
            backdropFilter: 'blur(8px)',
            border: `1px solid ${closeBtnBorder}`
          }}
          data-testid="close-summary-btn"
        >
          <X className="w-5 h-5" style={{ color: iconColor, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }} />
        </button>

        {/* Share this topic button — sits left of close */}
        {onShare && summary && (
          <button
            onClick={() => onShare(topic, summary)}
            className="absolute top-4 right-[4.25rem] p-2.5 rounded-full z-20 transition-all"
            style={{
              background: closeBtnBg,
              backdropFilter: 'blur(8px)',
              border: `1px solid ${closeBtnBorder}`,
            }}
            data-testid="share-topic-btn"
            aria-label={t('generated.recappage_share_this_topic_ffd7')}
            title={t('generated.recappage_share_this_topic_ffd7')}
          >
            <Share2 className="w-5 h-5" style={{ color: iconColor, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }} />
          </button>
        )}
        
        {/* Content */}
        <div className="relative p-6 pt-10">
          {/* Topic Label */}
          <div className="flex items-center gap-3 mb-5">
            <div 
              className="p-2.5 rounded-xl"
              style={{
                background: clearMode ? cardBg : 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                border: `1px solid ${cardBorder}`
              }}
            >
              <Sparkles className="w-6 h-6" style={{ color: iconColor, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }} />
            </div>
            <h2 
              className="text-2xl font-bold tracking-tight"
              style={{
                color: textColor,
                textShadow: clearMode ? 'none' : '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              {topic.label}
            </h2>
          </div>
          
          {loading ? (
            <div className="flex flex-col items-center py-10">
              <div 
                className="w-12 h-12 rounded-full animate-spin"
                style={{
                  border: `3px solid ${clearMode ? (themeColors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)') : 'rgba(255,255,255,0.2)'}`,
                  borderTopColor: textColor
                }}
              />
              <p className="mt-4" style={{ color: mutedColor }}>{t('common.loading')}</p>
            </div>
          ) : summary ? (
            <div className="space-y-5">
              {/* Highlight section */}
              <div 
                className="rounded-2xl p-5"
                style={{
                  background: cardBg,
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${cardBorder}`
                }}
              >
                <h3 
                  className="text-xs uppercase tracking-widest mb-2 font-semibold"
                  style={{ color: mutedColor }}
                >
                  {t('recap.highlight')}
                </h3>
                <p 
                  className="text-lg leading-relaxed"
                  style={{
                    color: textColor,
                    textShadow: clearMode ? 'none' : '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                >
                  {summary.highlight}
                </p>
              </div>
              
              {/* Key Points */}
              <div>
                <h3 
                  className="text-xs uppercase tracking-widest mb-3 font-semibold"
                  style={{ color: mutedColor }}
                >
                  {t('recap.key_points')}
                </h3>
                <ul className="space-y-3">
                  {summary.key_points?.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span 
                        className="w-2 h-2 rounded-full mt-2 shrink-0"
                        style={{ background: dotColor }}
                      />
                      <span 
                        className="leading-relaxed"
                        style={{
                          color: subTextColor,
                          textShadow: clearMode ? 'none' : '0 1px 2px rgba(0,0,0,0.15)'
                        }}
                      >
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Takeaways */}
              <div 
                className="rounded-2xl p-5"
                style={{
                  background: cardBg,
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${cardBorder}`
                }}
              >
                <h3 
                  className="text-xs uppercase tracking-widest mb-3 font-semibold"
                  style={{ color: mutedColor }}
                >
                  {t('recap.takeaways')}
                </h3>
                <ul className="space-y-3">
                  {summary.takeaways?.map((takeaway, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-lg leading-none">✨</span>
                      <span 
                        className="font-medium leading-relaxed"
                        style={{
                          color: textColor,
                          textShadow: clearMode ? 'none' : '0 1px 2px rgba(0,0,0,0.2)'
                        }}
                      >
                        {takeaway}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p style={{ color: mutedColor }} className="text-center py-10">
              {t('recap.no_summary')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Empty State Component with theme-aware colors
const EmptyState = ({ themeColors }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6">
      <div 
        className="w-28 h-28 rounded-full flex items-center justify-center mb-6"
        style={{
          background: `linear-gradient(135deg, 
            ${themeColors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'} 0%, 
            ${themeColors.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'} 100%)`,
          backdropFilter: 'blur(12px)',
          border: `1px solid ${themeColors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
        }}
      >
        <Sparkles 
          className="w-12 h-12" 
          style={{ color: themeColors.muted }}
        />
      </div>
      <h2 
        className="text-2xl font-bold mb-3"
        style={{ color: themeColors.primary }}
      >
        {t('recap.empty_title')}
      </h2>
      <p 
        className="mb-6 max-w-sm leading-relaxed"
        style={{ color: themeColors.secondary }}
      >
        {t('recap.empty_desc')}
      </p>
      <Button
        onClick={() => navigate('/app')}
        className="rounded-full px-8 py-2.5 transition-all"
        style={{
          background: themeColors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
          color: themeColors.primary,
          border: `1px solid ${themeColors.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
          backdropFilter: 'blur(8px)'
        }}
        data-testid="start-conversation-btn"
      >
        {t('recap.start_conversation')}
      </Button>
    </div>
  );
};

// Theme-aware background configurations
const getThemeGradient = (themeName, colorMode) => {
  const darkGradients = {
    light: { base: 'from-slate-900 via-blue-950 to-indigo-950', orb1: 'rgba(99, 102, 241, 0.2)', orb2: 'rgba(59, 130, 246, 0.15)', orb3: 'rgba(139, 92, 246, 0.12)' },
    dark: { base: 'from-slate-950 via-slate-900 to-indigo-950', orb1: 'rgba(99, 102, 241, 0.2)', orb2: 'rgba(59, 130, 246, 0.15)', orb3: 'rgba(139, 92, 246, 0.12)' },
    warm: { base: 'from-orange-950 via-amber-950 to-rose-950', orb1: 'rgba(251, 146, 60, 0.18)', orb2: 'rgba(249, 115, 22, 0.14)', orb3: 'rgba(234, 88, 12, 0.1)' },
    cool: { base: 'from-cyan-950 via-sky-950 to-blue-950', orb1: 'rgba(14, 165, 233, 0.18)', orb2: 'rgba(56, 189, 248, 0.14)', orb3: 'rgba(6, 182, 212, 0.1)' },
    forest: { base: 'from-emerald-950 via-green-900 to-teal-950', orb1: 'rgba(34, 197, 94, 0.18)', orb2: 'rgba(16, 185, 129, 0.14)', orb3: 'rgba(20, 184, 166, 0.1)' },
    sepia: { base: 'from-yellow-950 via-amber-950 to-orange-950', orb1: 'rgba(202, 138, 4, 0.18)', orb2: 'rgba(217, 119, 6, 0.14)', orb3: 'rgba(245, 158, 11, 0.1)' },
  };
  const lightGradients = {
    light: { base: 'from-slate-50 via-blue-50/80 to-indigo-50', orb1: 'rgba(99, 102, 241, 0.12)', orb2: 'rgba(59, 130, 246, 0.1)', orb3: 'rgba(139, 92, 246, 0.08)' },
    dark: { base: 'from-slate-50 via-violet-50/80 to-purple-50', orb1: 'rgba(139, 92, 246, 0.12)', orb2: 'rgba(168, 85, 247, 0.1)', orb3: 'rgba(99, 102, 241, 0.08)' },
    warm: { base: 'from-orange-50 via-amber-50/80 to-rose-50', orb1: 'rgba(251, 146, 60, 0.12)', orb2: 'rgba(249, 115, 22, 0.1)', orb3: 'rgba(234, 88, 12, 0.08)' },
    cool: { base: 'from-cyan-50 via-sky-50/80 to-blue-50', orb1: 'rgba(14, 165, 233, 0.12)', orb2: 'rgba(56, 189, 248, 0.1)', orb3: 'rgba(6, 182, 212, 0.08)' },
    forest: { base: 'from-emerald-50 via-green-50/80 to-teal-50', orb1: 'rgba(34, 197, 94, 0.12)', orb2: 'rgba(16, 185, 129, 0.1)', orb3: 'rgba(20, 184, 166, 0.08)' },
    sepia: { base: 'from-yellow-50 via-amber-50/80 to-orange-50', orb1: 'rgba(202, 138, 4, 0.12)', orb2: 'rgba(217, 119, 6, 0.1)', orb3: 'rgba(245, 158, 11, 0.08)' },
  };
  const gradients = colorMode === 'light' ? lightGradients : darkGradients;
  return gradients[themeName] || gradients.light;
};

// Main RecapPage Component
export default function RecapPage({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentTheme, colorMode } = useTheme();
  const [topics, setTopics] = useState([]);
  const [weekRange, setWeekRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryCache = useRef({});
  const [showBibleReader, setShowBibleReader] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [clearMode, setClearMode] = useState(false);
  // Share modal — opens themed Weekly Recap share card with 5-theme picker
  // and Share Image / Save / Link buttons (parity with DYK + reflection share).
  const [showShareRecap, setShowShareRecap] = useState(false);
  // Single-topic share: opens a themed card scoped to one bubble's summary.
  const [topicShareTarget, setTopicShareTarget] = useState(null);
  // Feature-flag gates: hide share buttons when their flags are OFF.
  const { enabled: topicShareEnabled } = useFeatureFlag('recap_topic_share');
  const { enabled: weeklyShareEnabled } = useFeatureFlag('recap_weekly_share');
  
  const handleNewChat = () => navigate('/chat');

  const handleToggleBibleReader = (val) => {
    const next = typeof val === 'function' ? val(showBibleReader) : val;
    if (next) setShowSettingsPanel(false);
    setShowBibleReader(next);
  };

  const handleToggleSettingsPanel = (val) => {
    const next = typeof val === 'function' ? val(showSettingsPanel) : val;
    if (next) setShowBibleReader(false);
    setShowSettingsPanel(next);
  };
  
  // Theme-aware colors
  const themeGradient = useMemo(() => getThemeGradient(currentTheme, colorMode), [currentTheme, colorMode]);
  const themeColors = useMemo(() => getThemeTextColors(colorMode), [colorMode]);
  
  // Fetch topics from live API
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await axios.get(`${API}/recap/topics`, { withCredentials: true });
        const fetchedTopics = response.data.topics || [];
        setTopics(fetchedTopics);
        setWeekRange({
          start: response.data.week_start,
          end: response.data.week_end
        });
        // Prefetch all summaries in parallel so bubble clicks are instant
        fetchedTopics.forEach(topic => {
          axios.get(
            `${API}/recap/summary/${topic.topic_id}?topic_label=${encodeURIComponent(topic.label)}`,
            { withCredentials: true }
          ).then(res => {
            summaryCache.current[topic.topic_id] = res.data;
          }).catch(() => {});
        });
      } catch (error) {
        console.error('Failed to fetch recap topics:', error);
        toast.error(t('recap.failed_load'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchTopics();
  }, [t]);
  
  // Handle bubble click — use prefetched cache if available
  const handleBubbleClick = useCallback(async (topic) => {
    setSelectedTopic(topic);
    setSummary(null);

    // Check prefetch cache first
    if (summaryCache.current[topic.topic_id]) {
      setSummary(summaryCache.current[topic.topic_id]);
      return;
    }

    setSummaryLoading(true);
    
    try {
      const response = await axios.get(
        `${API}/recap/summary/${topic.topic_id}?topic_label=${encodeURIComponent(topic.label)}`,
        { withCredentials: true }
      );
      setSummary(response.data);
      summaryCache.current[topic.topic_id] = response.data;
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      setTopics(prev => prev.filter(t => t.topic_id !== topic.topic_id));
      setSelectedTopic(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);
  
  const handleCloseModal = useCallback(() => {
    setSelectedTopic(null);
    setSummary(null);
  }, []);
  
  const needsScroll = topics.length > 10;
  
  return (
    <div className={`page-container bg-gradient-to-br overflow-x-hidden ${themeGradient.base}`}>
      <Header 
        user={user} 
        showBibleReader={showBibleReader}
        setShowBibleReader={handleToggleBibleReader}
        showSettingsPanel={showSettingsPanel}
        setShowSettingsPanel={handleToggleSettingsPanel}
        onNewChat={handleNewChat}
      />
      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden flex">
        {/* Bubbles Area */}
        <div className="flex-1 relative overflow-hidden">
        {/* Animated Background Orbs - subtle color shifting */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute w-[600px] h-[600px] rounded-full"
            style={{ 
              background: `radial-gradient(circle, ${themeGradient.orb1} 0%, transparent 70%)`,
              top: '5%',
              left: '10%',
              filter: 'blur(60px)',
              animation: 'orbFloat1 30s ease-in-out infinite'
            }} 
          />
          <div 
            className="absolute w-[500px] h-[500px] rounded-full"
            style={{ 
              background: `radial-gradient(circle, ${themeGradient.orb2} 0%, transparent 70%)`,
              bottom: '10%',
              right: '15%',
              filter: 'blur(50px)',
              animation: 'orbFloat2 35s ease-in-out infinite'
            }} 
          />
          <div 
            className="absolute w-[400px] h-[400px] rounded-full"
            style={{ 
              background: `radial-gradient(circle, ${themeGradient.orb3} 0%, transparent 70%)`,
              top: '45%',
              left: '45%',
              transform: 'translate(-50%, -50%)',
              filter: 'blur(40px)',
              animation: 'orbFloat3 25s ease-in-out infinite'
            }} 
          />
        </div>
        
        {/* Week Header */}
        <div className="relative z-10 text-center pt-8 pb-4 px-4">
          <h1 
            className="text-3xl md:text-4xl font-bold mb-2 tracking-tight"
            style={{ 
              color: themeColors.primary,
              textShadow: themeColors.isDark ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
            }}
          >
            {t('recap.title')}
          </h1>
          {weekRange.start && weekRange.end && (
            <p 
              className="text-sm md:text-base font-light mb-3"
              style={{ color: themeColors.muted }}
            >
              {weekRange.start} - {weekRange.end}
            </p>
          )}
          {/* Color / Clear toggle */}
          <div className="inline-flex items-center rounded-full p-0.5" style={{
            background: themeColors.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            border: `1px solid ${themeColors.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`
          }}>
            <button
              onClick={() => setClearMode(false)}
              className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                background: !clearMode
                  ? (themeColors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)')
                  : 'transparent',
                color: !clearMode ? themeColors.primary : themeColors.muted
              }}
              data-testid="recap-mode-color"
            >
              {t('recap.mode_color', 'Color')}
            </button>
            <button
              onClick={() => setClearMode(true)}
              className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                background: clearMode
                  ? (themeColors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)')
                  : 'transparent',
                color: clearMode ? themeColors.primary : themeColors.muted
              }}
              data-testid="recap-mode-clear"
            >
              {t('recap.mode_clear', 'Clear')}
            </button>
          </div>
        </div>
        
        {/* Bubbles Container */}
        <div 
          className={`relative z-10 ${needsScroll ? 'overflow-y-auto' : ''}`}
          style={{ 
            height: needsScroll ? 'calc(100dvh - 200px)' : 'auto'
          }}
        >
          <div 
            className="relative w-full"
            style={{ 
              height: needsScroll ? `${Math.max(600, topics.length * 80)}px` : 'calc(100dvh - 220px)',
              minHeight: '500px'
            }}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                {/* Skeleton bubbles for a more polished loading experience */}
                {[
                  { x: 30, y: 35, size: 130 },
                  { x: 65, y: 25, size: 100 },
                  { x: 50, y: 60, size: 110 },
                  { x: 20, y: 65, size: 80 },
                  { x: 75, y: 55, size: 90 },
                ].map((b, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full animate-pulse"
                    style={{
                      left: `${b.x}%`,
                      top: `${b.y}%`,
                      width: b.size,
                      height: b.size,
                      transform: 'translate(-50%, -50%)',
                      background: themeColors.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      border: `1px solid ${themeColors.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
                <p className="absolute bottom-8 text-sm" style={{ color: themeColors.muted }}>
                  {t('recap.loading', 'Preparing your recap...')}
                </p>
              </div>
            ) : topics.length === 0 ? (
              <EmptyState themeColors={themeColors} />
            ) : (
              topics.map((bubble, index) => (
                <GlassBubble
                  key={bubble.topic_id}
                  bubble={bubble}
                  index={index}
                  totalBubbles={topics.length}
                  onClick={handleBubbleClick}
                  themeColors={themeColors}
                  clearMode={clearMode}
                />
              ))
            )}
          </div>
        </div>
        
        {/* Hint Text + Share */}
        {!loading && topics.length > 0 && (
          <div className="relative z-10 text-center pb-6 flex flex-col items-center gap-4">
            <p 
              className="text-sm font-light"
              style={{ color: themeColors.muted }}
            >
              {t('recap.tap_hint')}
            </p>
            {weeklyShareEnabled && (
            <button
              onClick={() => setShowShareRecap(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'rgba(99,102,241,0.15)',
                color: themeColors.primary,
                border: '1px solid rgba(99,102,241,0.3)',
              }}
              data-testid="recap-share-btn"
            >
              <Share2 className="h-4 w-4" />
              {t('generated.recappage_share_your_week_a809')}
            </button>
            )}
          </div>
        )}
      </div>

      {/* Bible Reader Panel */}
      {showBibleReader && (
        <div className="w-full lg:w-[600px] border-l theme-border theme-surface shadow-xl z-30 absolute top-0 right-0 h-full lg:static bg-background">
          <BibleReader
            onClose={() => setShowBibleReader(false)}
          />
        </div>
      )}

      {/* Settings Panel */}
      {showSettingsPanel && (
        <div className="absolute top-0 right-0 h-full w-full lg:static lg:w-auto z-30 border-l theme-border theme-surface shadow-xl lg:shadow-none">
          <SettingsPage
            user={user}
            asPanel={true}
            onClose={() => setShowSettingsPanel(false)}
          />
        </div>
      )}
      </div>
      {/* Summary Modal */}
      <GlassSummaryModal
        topic={selectedTopic}
        summary={summary}
        loading={summaryLoading}
        onClose={handleCloseModal}
        onShare={topicShareEnabled ? (t, s) => setTopicShareTarget({ topic: t, summary: s }) : null}
        themeColors={themeColors}
        clearMode={clearMode}
      />
      {/* Share single topic modal */}
      {topicShareTarget && (
        <ShareRecapTopicModal
          label={topicShareTarget.topic?.label || ''}
          highlight={topicShareTarget.summary?.highlight || ''}
          keyPoints={topicShareTarget.summary?.key_points || []}
          takeaways={topicShareTarget.summary?.takeaways || []}
          weekStart={weekRange.start}
          weekEnd={weekRange.end}
          bubbleColor={topicShareTarget.topic?.color}
          onClose={() => setTopicShareTarget(null)}
        />
      )}
      {/* Share Recap modal */}
      {showShareRecap && (
        <ShareRecapModal
          topics={topics}
          weekStart={weekRange.start}
          weekEnd={weekRange.end}
          onClose={() => setShowShareRecap(false)}
        />
      )}
      {/* Smooth keyframe animations */}
      <style>{`
        @keyframes orbFloat1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, -30px) scale(1.05); }
          66% { transform: translate(-30px, 20px) scale(0.97); }
        }
        @keyframes orbFloat2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-35px, 25px) scale(0.96); }
          66% { transform: translate(25px, -35px) scale(1.04); }
        }
        @keyframes orbFloat3 {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.08); }
        }
      `}</style>
    </div>
  );
}
